import type { AppBskyActorDefs } from '@atcute/bluesky';
import type { LabelPreference } from '@atcute/bluesky-moderation';
import type { Did } from '@atcute/lexicons';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { bskyFeedUri } from '#/lib/constants/feeds';
import { APP_LABELERS } from '#/lib/moderation/labelers';
import type { BskyFeedViewPreference } from '#/lib/moderation/preferences-types';
import { replaceEqualDeep } from '#/lib/utils/objects';

import { GCTIME, STALE } from '#/state/queries';
import {
	addSavedFeeds,
	getPreferences,
	overwriteSavedFeeds,
	removeContentLabelPref,
	removeMutedWord,
	removeMutedWords,
	removeSavedFeeds,
	setAdultContentEnabled,
	setContentLabelPref,
	setFeedViewPrefs,
	setAppSpecificPrefs,
	setThreadViewPrefs,
	setVerificationPrefs,
	updateMutedWord,
	updateSavedFeeds,
	upsertMutedWords,
} from '#/state/queries/preferences/agent';
import type { AppSpecificPrefs } from '#/state/queries/preferences/app-specific-prefs';
import {
	DEFAULT_HOME_FEED_PREFS,
	DEFAULT_LOGGED_OUT_PREFERENCES,
	DEFAULT_THREAD_VIEW_PREFS,
} from '#/state/queries/preferences/const';
import type { ThreadViewPreferences, UsePreferencesQueryResponse } from '#/state/queries/preferences/types';
import { createQueryPersister } from '#/state/query-persister';
import { getClients, useSession } from '#/state/session';
import { saveSubscribedLabelers } from '#/state/session/labelers';

import * as Toast from '#/components/Toast';

import { m } from '#/paraglide/messages';

export * from '#/state/queries/preferences/const';
export * from '#/state/queries/preferences/moderation';
export * from '#/state/queries/preferences/types';

export const preferencesQueryKey = ['getPreferences'];
const preferencesQueryPersister = createQueryPersister({ version: 1 });

export function usePreferencesQuery() {
	const { pds } = getClients();
	const { currentAccount } = useSession();

	const query = useQuery({
		queryKey: preferencesQueryKey,
		staleTime: STALE.MINUTES.FIVE,
		gcTime: GCTIME.DAYS.SEVEN,
		refetchOnWindowFocus: true,
		queryFn: async ({ signal }) => {
			if (!pds || !currentAccount) {
				return DEFAULT_LOGGED_OUT_PREFERENCES;
			} else {
				const res = await getPreferences(pds, APP_LABELERS, signal);

				saveSubscribedLabelers(
					currentAccount.did,
					res.moderationPrefs.labelers.map((l) => l.did),
				);

				const preferences: UsePreferencesQueryResponse = {
					...res,
					savedFeeds: res.savedFeeds.filter((f) => f.type !== 'unknown'),
					/** Special preference, only used for following feed, previously called `home` */
					feedViewPrefs: {
						...DEFAULT_HOME_FEED_PREFS,
						...res.feedViewPrefs.home,
					},
					threadViewPrefs: {
						...DEFAULT_THREAD_VIEW_PREFS,
						...res.threadViewPrefs,
					},
				};
				return preferences;
			}
		},
		persister: preferencesQueryPersister,
		structuralSharing: replaceEqualDeep,
	});

	return query;
}

export function usePreferencesSetContentLabelMutation() {
	const { pds } = getClients();
	const queryClient = useQueryClient();

	return useMutation<
		void,
		unknown,
		// `visibility: undefined` clears the preference
		{ label: string; visibility: LabelPreference | undefined; labelerDid: Did | undefined }
	>({
		mutationFn: async ({ label, visibility, labelerDid }) => {
			if (visibility === undefined) {
				await removeContentLabelPref(pds!, label, labelerDid);
			} else {
				await setContentLabelPref(pds!, label, visibility, labelerDid);
			}
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function usePreferencesSetAdultContentMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, { enabled: boolean }>({
		mutationFn: async ({ enabled }) => {
			await setAdultContentEnabled(pds!, enabled);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useSetFeedViewPreferencesMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, Partial<BskyFeedViewPreference>>({
		mutationFn: async (prefs) => {
			/*
			 * special handling here, merged into `feedViewPrefs` above, since
			 * following was previously called `home`
			 */
			await setFeedViewPrefs(pds!, 'home', prefs);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useSetThreadViewPreferencesMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: void, variables: Partial<ThreadViewPreferences>) => void;
	onError?: (error: unknown) => void;
}) {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, Partial<ThreadViewPreferences>>({
		mutationFn: async (prefs) => {
			await setThreadViewPrefs(pds!, prefs);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
		onSuccess,
		onError,
	});
}

export function useOverwriteSavedFeedsMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, AppBskyActorDefs.SavedFeed[]>({
		mutationFn: async (savedFeeds) => {
			await overwriteSavedFeeds(pds!, savedFeeds);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useAddSavedFeedsMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, Pick<AppBskyActorDefs.SavedFeed, 'type' | 'value' | 'pinned'>[]>({
		mutationFn: async (savedFeeds) => {
			await addSavedFeeds(pds!, savedFeeds);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useRemoveFeedMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, Pick<AppBskyActorDefs.SavedFeed, 'id'>>({
		mutationFn: async (savedFeed) => {
			await removeSavedFeeds(pds!, [savedFeed.id]);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

/**
 * tracks whether a feed or list is in the user's saved feeds and toggles it, surfacing a toast on either
 * outcome.
 *
 * @param pin whether a freshly saved feed should also be pinned
 * @param type the saved-feed kind, `feed` for a generator or `list` for a list
 * @param uri the feed generator or list URI, matched against the saved-feeds config
 * @returns an object containing the current saved state, a pending state, and a function to toggle the save
 *   state
 */
export function useToggleSavedFeed({
	pin,
	type,
	uri,
}: {
	pin?: boolean;
	type: 'feed' | 'list';
	uri: string;
}) {
	const { data: preferences } = usePreferencesQuery();
	const { isPending: isAddPending, mutateAsync: saveFeeds } = useAddSavedFeedsMutation();
	const { isPending: isRemovePending, mutateAsync: removeFeed } = useRemoveFeedMutation();

	const savedFeedConfig = preferences?.savedFeeds?.find((feed) => feed.value === uri);

	const toggleSave = async () => {
		try {
			if (savedFeedConfig) {
				await removeFeed(savedFeedConfig);
			} else {
				await saveFeeds([{ pinned: !!pin, type, value: uri }]);
			}
			Toast.show(m['common.feeds.updatedToast']());
		} catch (err) {
			console.error('failed to update saved feeds', pin, err);
			Toast.show(m['state.feeds.error.update'](), { type: 'error' });
		}
	};

	return {
		isPending: isAddPending || isRemovePending,
		isSaved: !!savedFeedConfig,
		toggleSave,
	};
}

export function useReplaceForYouWithDiscoverFeedMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation({
		mutationFn: async ({
			forYouFeedConfig,
			discoverFeedConfig,
		}: {
			forYouFeedConfig: AppBskyActorDefs.SavedFeed | undefined;
			discoverFeedConfig: AppBskyActorDefs.SavedFeed | undefined;
		}) => {
			if (forYouFeedConfig) {
				await removeSavedFeeds(pds!, [forYouFeedConfig.id]);
			}
			if (!discoverFeedConfig) {
				await addSavedFeeds(pds!, [
					{
						type: 'feed',
						value: bskyFeedUri('whats-hot'),
						pinned: true,
					},
				]);
			} else {
				await updateSavedFeeds(pds!, [
					{
						...discoverFeedConfig,
						pinned: true,
					},
				]);
			}
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useUpdateSavedFeedsMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, AppBskyActorDefs.SavedFeed[]>({
		mutationFn: async (feeds) => {
			await updateSavedFeeds(pds!, feeds);

			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useUpsertMutedWordsMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation({
		mutationFn: async (mutedWords: AppBskyActorDefs.MutedWord[]) => {
			await upsertMutedWords(pds!, mutedWords);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useUpdateMutedWordMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation({
		mutationFn: async (mutedWord: AppBskyActorDefs.MutedWord) => {
			await updateMutedWord(pds!, mutedWord);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useRemoveMutedWordMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation({
		mutationFn: async (mutedWord: AppBskyActorDefs.MutedWord) => {
			await removeMutedWord(pds!, mutedWord);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useRemoveMutedWordsMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation({
		mutationFn: async (mutedWords: AppBskyActorDefs.MutedWord[]) => {
			await removeMutedWords(pds!, mutedWords);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useSetAppSpecificPrefsMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, Partial<Omit<AppSpecificPrefs, '$type'>>>({
		mutationFn: async (patch) => {
			await setAppSpecificPrefs(pds!, patch);
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}

export function useSetVerificationPrefsMutation() {
	const queryClient = useQueryClient();
	const { pds } = getClients();

	return useMutation<void, unknown, AppBskyActorDefs.VerificationPrefs>({
		mutationFn: async (prefs) => {
			await setVerificationPrefs(pds!, prefs);
			// triggers a refetch
			await queryClient.invalidateQueries({
				queryKey: preferencesQueryKey,
			});
		},
	});
}
